import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, getReturnTo } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { WeeklyIdeasOptIn } from "@/components/WeeklyIdeasOptIn";

// Set to false to temporarily close new self-service sign-ups (login stays open).
const REGISTRATION_OPEN = true;

const PLAN_INFO = {
  start: { name: "Start", price: "395 kr." },
  pro: { name: "Pro", price: "995 kr." },
  business: { name: "Business", price: "2.495 kr." },
};

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register, verifyEmail, resendCode } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [code, setCode] = useState("");
  const [ideaMail, setIdeaMail] = useState(false);

  const planParam = searchParams.get("plan");
  const trialParam = searchParams.get("trial") === "1";
  const planInfo = PLAN_INFO[planParam];

  const goAfterAuth = (user) => {
    if (user.role === "admin") navigate("/admin");
    else navigate(user.onboarded ? (getReturnTo() || "/dashboard") : "/kom-i-gang");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const plan = searchParams.get("plan");
      const wantsTrial = searchParams.get("trial") === "1";
      if (!isLogin && ["start", "pro", "business"].includes(plan)) {
        localStorage.setItem("aicom_intended_plan", plan);
        localStorage.setItem("aicom_intended_trial", wantsTrial ? "1" : "0");
      }
      const res = isLogin ? await login(email, password) : await register(name, email, password, null, ideaMail);
      if (res && res.needs_verification) {
        setPendingEmail(res.email || email);
        setCode("");
        toast.success("Vi har sendt en 4-cifret kode til din e-mail");
        return;
      }
      toast.success(isLogin ? "Velkommen tilbage" : "Bruger oprettet");
      goAfterAuth(res);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await verifyEmail(pendingEmail, code);
      toast.success("E-mail bekræftet");
      goAfterAuth(user);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try { await resendCode(pendingEmail); toast.success("Ny kode sendt"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative px-6" data-clarity-mask="true">
      <div className="grain" />
      <Link to="/" className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="auth-back-home">
        <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
      </Link>
      <div className="relative z-10 w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center mb-8">
          <span className="font-display text-2xl font-bold tracking-tight"><span style={{ color: "#22D3C5" }}>AI</span>COM</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-7 sm:p-8" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03), 0 24px 50px -34px rgba(17,17,17,0.2)" }}>
        {pendingEmail ? (
          <div data-testid="verify-email-step">
            <h1 className="font-display text-3xl font-medium tracking-tight text-center">Bekræft din e-mail</h1>
            <p className="text-muted-foreground text-center mt-2 text-sm">
              Vi har sendt en 4-cifret kode til <span className="text-foreground font-medium">{pendingEmail}</span>. Indtast den nedenfor.
            </p>
            <form onSubmit={submitCode} className="mt-8 space-y-4">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric"
                autoFocus
                maxLength={4}
                placeholder="••••"
                className="text-center text-2xl tracking-[0.5em] h-14"
                data-testid="verify-code-input"
                required
              />
              <Button type="submit" className="w-full h-11" disabled={loading || code.length !== 4} data-testid="verify-submit-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Bekræft e-mail
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Fik du ingen kode?{" "}
              <button type="button" onClick={resend} className="text-foreground hover:underline" data-testid="verify-resend-btn">Send igen</button>
            </p>
            <p className="text-center text-sm text-muted-foreground mt-2">
              <button type="button" onClick={() => setPendingEmail(null)} className="hover:text-foreground transition-colors" data-testid="verify-back-btn">← Tilbage</button>
            </p>
          </div>
        ) : !isLogin && !REGISTRATION_OPEN ? (
          <div className="text-center" data-testid="registration-closed">
            <h1 className="font-display text-3xl font-medium tracking-tight">Oprettelse er midlertidigt lukket</h1>
            <p className="text-muted-foreground mt-3 text-sm">
              Vi tager ikke imod nye tilmeldinger lige nu. Er du interesseret i Aicom AI, er du velkommen til at skrive til os på{" "}
              <a href="mailto:hello@aicom.dk" className="text-foreground hover:underline">hello@aicom.dk</a>, så vender vi tilbage.
            </p>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Har du allerede en bruger?{" "}
              <Link to="/login" className="text-foreground hover:underline" data-testid="auth-switch-link">Log ind</Link>
            </p>
          </div>
        ) : (
        <>
        <h1 className="font-display text-3xl font-medium tracking-tight text-center">
          {isLogin ? "Log ind" : "Opret bruger"}
        </h1>
        <p className="text-muted-foreground text-center mt-2 text-sm">
          {isLogin ? "Fortsæt til din virksomheds digitale kommunikationsrådgiver" : "Kom i gang på under et minut"}
        </p>

        {!isLogin && planInfo && (
          <div className="mt-6 rounded-xl border border-border bg-secondary/50 p-4" data-testid="auth-plan-info">
            {trialParam ? (
              <>
                <p className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4 shrink-0" style={{ color: "#22D3C5" }} />
                  30 dages gratis prøveperiode af {planInfo.name}
                </p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Der trækkes ikke betaling nu. Når de 30 dage er gået, starter abonnementsbetalingen
                  automatisk ({planInfo.price}/md. inkl. moms), <span className="text-foreground font-medium">medmindre du opsiger inden prøveperiodens udløb</span>. Du kan opsige når som helst. Dit betalingskort indtastes ved tilmelding.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">Abonnement: {planInfo.name}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Du opretter abonnementet til {planInfo.price}/md. inkl. moms. Betalingen gennemføres ved tilmelding, og abonnementet fornyes automatisk hver måned, indtil det opsiges.
                </p>
              </>
            )}
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Dit navn</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
                data-testid="auth-name-input" placeholder="Fx dit fulde navn" />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="auth-email-input" placeholder="dig@virksomhed.dk" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Adgangskode</Label>
              {isLogin && (
                <Link to="/glemt-adgangskode" className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="forgot-password-link">
                  Glemt adgangskode?
                </Link>
              )}
            </div>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="auth-password-input" placeholder="••••••••" />
          </div>
          {!isLogin && <WeeklyIdeasOptIn checked={ideaMail} onChange={setIdeaMail} />}
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="auth-submit-btn">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isLogin ? "Log ind" : "Opret bruger"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isLogin ? "Ny hos Aicom? " : "Har du allerede en bruger? "}
          <Link to={isLogin ? "/register" : "/login"} className="text-foreground hover:underline" data-testid="auth-switch-link">
            {isLogin ? "Opret bruger" : "Log ind"}
          </Link>
        </p>
        </>
        )}
        </div>
      </div>
    </div>
  );
}
